import {
    AccessTime as AccessTimeIcon,
    ArrowBack as ArrowBackIcon,
    CalendarToday as CalendarIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    AttachMoney as MoneyIcon,
    Notes as NotesIcon,
    Payment as PaymentIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppointmentModal from '../components/AppointmentModal';
import Loading from '../components/Loading';
import { useToast } from '../contexts/ToastContext';
import { Appointment, appointmentApi } from '../services/appointmentApi';
import { Client, clientApi } from '../services/clientApi';
import { colors } from '../theme';

const DietitianAppointmentDetailPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const statusChipStyle = useMemo(() => {
    const status = appointment?.status;
    switch (status) {
      case 'Tamamlandı':
        return { bgcolor: colors.primary.dark };
      case 'İptal Edildi':
        return { bgcolor: colors.status.error };
      case 'Ertelendi':
        return { bgcolor: colors.status.warning };
      default:
        return { bgcolor: colors.primary.main };
    }
  }, [appointment?.status]);

  useEffect(() => {
    const fetchAppointmentData = async () => {
      if (!appointmentId) return;

      try {
        setLoading(true);
        const appointmentData = await appointmentApi.getAppointmentById(
          appointmentId
        );
        setAppointment(appointmentData);

        // Client bilgisini getir
        if (appointmentData.clientId) {
          try {
            const clientData = await clientApi.getClientById(
              appointmentData.clientId
            );
            setClient(clientData);
          } catch (err) {
            console.error('Error fetching client:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching appointment data:', err);
        setError('Randevu bilgileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentData();
  }, [appointmentId]);

  const formatDateForInput = (dateValue?: string) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const editingData = useMemo(() => {
    if (!appointment) return null;
    return {
      id: appointment.id,
      dietitianId: appointment.dietitianId || '',
      clientId: appointment.clientId,
      clientName: appointment.clientName,
      appointmentDate: formatDateForInput(appointment.appointmentDate),
      startTime: appointment.startTime || '',
      endTime: appointment.endTime || '',
      type: appointment.type || '',
      status: appointment.status || 'Planlandı',
      notes: appointment.notes || '',
      location: appointment.location || '',
      fee: appointment.fee || 0,
      isPaid: appointment.isPaid,
    };
  }, [appointment]);

  const handleUpdateAppointment = async (data: any) => {
    if (!appointmentId) return;
    try {
      const payload = {
        clientId: data.clientId,
        clientName: data.clientName,
        appointmentDate: data.appointmentDate,
        startTime: data.startTime,
        endTime: data.endTime,
        type: data.type,
        status: data.status,
        notes: data.notes,
        location: data.location,
        fee: data.fee,
        isPaid: data.isPaid,
      };

      const response = await appointmentApi.updateAppointment(
        appointmentId,
        payload
      );
      setAppointment(response.appointment);

      if (payload.clientId && payload.clientId !== appointment?.clientId) {
        try {
          const updatedClient = await clientApi.getClientById(payload.clientId);
          setClient(updatedClient);
        } catch (err) {
          console.error('Error fetching updated client:', err);
        }
      }

      showSuccess('Randevu güncellendi', 'Güncelleme Başarılı');
    } catch (err) {
      console.error('Error updating appointment:', err);
      showError('Randevu güncellenemedi', 'Hata');
      throw err;
    }
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Randevu bilgileri yükleniyor..."
      />
    );
  }

  if (error || !appointment) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error || 'Randevu bulunamadı'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dietitian/appointments')}
          sx={{
            mt: 2,
            bgcolor: colors.primary.main,
            '&:hover': { bgcolor: colors.hover.primary },
          }}
        >
          Randevular Listesine Dön
        </Button>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'auto',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 1.5,
          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(46, 125, 50, 0.25)',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={0.5}
          gap={0.75}
        >
          <Box display="flex" alignItems="center" gap={0.75}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dietitian/appointments')}
              sx={{
                color: 'white',
                textTransform: 'none',
                px: 0.5,
                minHeight: 32,
                minWidth: 36,
                borderRadius: 1.25,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
              }}
            ></Button>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ minWidth: 140 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: 'white', lineHeight: 1.2 }}
              >
                {appointment.clientName}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ minWidth: 110, justifyContent: 'flex-end' }}
          >
            {appointment.status && (
              <Chip
                label={appointment.status}
                size="small"
                sx={{
                  ...statusChipStyle,
                  color: 'white',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  height: 26,
                  borderRadius: 999,
                  px: 2,
                  mr: 1,
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* Randevu Detayları */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 0 }}>
            Detaylar
          </Typography>
          <Button
            variant="contained"
            onClick={() => setIsEditModalOpen(true)}
            sx={{
              bgcolor: colors.primary.main,
              color: 'white',
              textTransform: 'none',
              boxShadow: '0 2px 6px rgba(67,160,71,0.2)',
              minHeight: 34,
              width: 100,
              px: 1.5,
              '&:hover': { bgcolor: colors.hover.primary },
            }}
          >
            Düzenle
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <CalendarIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                Tarih
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={600}>
              {new Date(appointment.appointmentDate).toLocaleDateString(
                'tr-TR'
              )}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <AccessTimeIcon
                sx={{ fontSize: 18, color: colors.primary.main }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                Saat
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={600}>
              {appointment.startTime} - {appointment.endTime}
            </Typography>
          </Box>

          {appointment.type && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: colors.background.light,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
                display="block"
                mb={0.5}
              >
                Tür
              </Typography>
              <Chip
                label={appointment.type}
                size="small"
                sx={{
                  bgcolor: colors.primary.light,
                  color: colors.primary.dark,
                  fontWeight: 600,
                  height: 24,
                }}
              />
            </Box>
          )}

          {appointment.location && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: colors.background.light,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <LocationIcon
                  sx={{ fontSize: 18, color: colors.primary.main }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Konum
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {appointment.location}
              </Typography>
            </Box>
          )}

          {appointment.fee && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: colors.background.light,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <MoneyIcon sx={{ fontSize: 18, color: colors.primary.main }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Ücret
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                ₺{appointment.fee}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <PaymentIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                Ödeme
              </Typography>
            </Box>
            <Chip
              label={appointment.isPaid ? 'Ödendi' : 'Ödenmedi'}
              size="small"
              sx={{
                bgcolor: appointment.isPaid
                  ? colors.primary.main
                  : colors.text.secondary,
                color: 'white',
                fontWeight: 600,
                height: 24,
              }}
            />
          </Box>
        </Box>

        {appointment.notes && (
          <Box
            sx={{
              p: 2,
              mt: 2,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <NotesIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Notlar
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {appointment.notes}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Danışan Bilgileri */}
      {client && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
            Danışan
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: colors.hover.background,
                borderColor: colors.primary.main,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            }}
            onClick={() => navigate(`/dietitian/clients/${client.id}`)}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: colors.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                <PersonIcon />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {client.firstName} {client.lastName}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon
                  sx={{ fontSize: 16, color: colors.text.secondary }}
                />
                <Typography variant="body2" color="text.secondary">
                  {client.email}
                </Typography>
              </Box>
              {client.phone && (
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon
                    sx={{ fontSize: 16, color: colors.text.secondary }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {client.phone}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      <AppointmentModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleUpdateAppointment}
        clients={client ? [client] : []}
        editingAppointment={editingData || undefined}
      />
    </Box>
  );
};

export default DietitianAppointmentDetailPage;
